import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Badge,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Chat as ChatIcon,
  Notifications as NotificationsIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';

const API_BASE_URL = 'https://agent.1lb6ey6g0jkt.iconaccounting.ie';

const SupportDashboard = () => {
  const [waitingChats, setWaitingChats] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [socket, setSocket] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioContext, setAudioContext] = useState(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const titleFlashIntervalRef = useRef(null);
  const titleFlashTimeoutRef = useRef(null);
  const originalTitleRef = useRef(document.title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ label: '', percent: 0, color: '#e5e7eb' });
  const [lastUpdate, setLastUpdate] = useState(null);
  const navigate = useNavigate();

  // Password strength evaluator (length, cases, digits, symbols)
  const evaluatePasswordStrength = (value) => {
    if (!value) return { label: '', percent: 0, color: '#e5e7eb' };
    let score = 0;
    if (value.length >= 6) score += 1;
    if (value.length >= 10) score += 1;
    if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;
    const percent = Math.min(100, (score / 5) * 100);
    let label = 'Weak';
    let color = '#ef4444';
    if (score >= 4) { label = 'Strong'; color = '#10b981'; }
    else if (score === 3) { label = 'Good'; color = '#f59e0b'; }
    return { label, percent, color };
  };

  // Check authentication and get user info
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        console.log('👤 User authenticated:', response.data.username);
      } catch (error) {
        console.error('Authentication failed:', error);
        localStorage.removeItem('authToken');
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  // Initialize audio context for notification sounds
  useEffect(() => {
    const initAudio = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContext();
        setAudioContext(context);
        audioContextRef.current = context;
        console.log('🔊 Audio context initialized successfully');
      } catch (error) {
        console.error('Audio context not supported:', error);
      }
    };

    initAudio();
  }, []);

  // One-time audio unlock on first user interaction
  useEffect(() => {
    const unlock = () => {
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          audioUnlockedRef.current = true;
          console.log('🔓 Audio context unlocked');
        }).catch(() => {});
      } else {
        audioUnlockedRef.current = true;
      }
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [audioContext]);

  // Play notification sound with better audio
  const playNotificationSound = () => {
    if (!soundEnabled) {
      console.log('🔇 Sound is disabled');
      return;
    }

    const ctx = audioContextRef.current;
    if (!ctx) {
      console.log('🔇 Audio context not available');
      return;
    }

    try {
      // Resume audio context if suspended (required by browsers)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      // Create a more noticeable "ding dong" sound (louder)
      const playTone = (frequency, duration, delay = 0) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
        oscillator.type = 'sine';

        // Better volume envelope
        gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
        gainNode.gain.linearRampToValueAtTime(0.75, ctx.currentTime + delay + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.03, ctx.currentTime + delay + duration);

        oscillator.start(ctx.currentTime + delay);
        oscillator.stop(ctx.currentTime + delay + duration);
      };

      // Play "ding dong" sound (two tones)
      playTone(800, 0.3, 0);    // First tone (ding)
      playTone(600, 0.3, 0.4);  // Second tone (dong)

      console.log('🔔 Ding-dong notification sound played');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Tab title flash utilities (flash even if tab focused, stop after duration)
  const startTitleFlash = (text = 'New message!', durationMs = 6000) => {
    if (titleFlashIntervalRef.current) return;
    originalTitleRef.current = document.title;
    let toggle = false;
    titleFlashIntervalRef.current = setInterval(() => {
      document.title = toggle ? text : originalTitleRef.current;
      toggle = !toggle;
    }, 700);
    if (titleFlashTimeoutRef.current) clearTimeout(titleFlashTimeoutRef.current);
    titleFlashTimeoutRef.current = setTimeout(() => {
      stopTitleFlash();
    }, durationMs);
  };

  const stopTitleFlash = () => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
      document.title = originalTitleRef.current;
    }
    if (titleFlashTimeoutRef.current) {
      clearTimeout(titleFlashTimeoutRef.current);
      titleFlashTimeoutRef.current = null;
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    if (socket) {
      socket.disconnect();
    }
    navigate('/login');
  };

  // Change password function
  const handleChangePassword = () => {
    setShowChangePassword(true);
    setChangePasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setChangePasswordError('');
    setChangePasswordSuccess('');
  };

  const handleChangePasswordSubmit = async () => {
    // Validation
    if (!changePasswordData.currentPassword || !changePasswordData.newPassword || !changePasswordData.confirmPassword) {
      setChangePasswordError('All fields are required');
      return;
    }

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }

    if (changePasswordData.newPassword.length < 6) {
      setChangePasswordError('New password must be at least 6 characters long');
      return;
    }

    try {
      setIsChangingPassword(true);
      setChangePasswordError('');
      
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword: changePasswordData.currentPassword,
        newPassword: changePasswordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChangePasswordSuccess('Password changed successfully!');
      setChangePasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close dialog after 2 seconds
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Change password error:', error);
      setChangePasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleChangePasswordClose = () => {
    setShowChangePassword(false);
    setChangePasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setChangePasswordError('');
    setChangePasswordSuccess('');
  };

  // Initial data fetch - only once on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // WebSocket connection and event handling
  useEffect(() => {
    connectSocket();
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const connectSocket = () => {
    if (socket) {
      socket.disconnect();
    }

    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('🔌 Socket connected');
      const token = localStorage.getItem('authToken');
      if (token) newSocket.emit('join_support', token);
    });

    // Handle new chat started
    newSocket.on('new_chat_started', (data) => {
      console.log('🆕 New chat started:', data);
      playNotificationSound();
      startTitleFlash('New waiting chat');
      // Only fetch waiting chats since that's what changed
      fetchWaitingChats();
    });

    // Handle new user message
    newSocket.on('new_message', (data) => {
      console.log('💬 New message received:', data);
      playNotificationSound();
      startTitleFlash('New customer message');
      // Fetch both waiting and active chats since messages can affect both
      fetchWaitingAndActiveChats();
    });

    // Handle chat ended
    newSocket.on('chat_ended', (data) => {
      console.log('🔚 Chat ended event received:', data);
      console.log('🔄 Refreshing all data including transcripts...');
      // Fetch all data since chat status changed
      fetchAllData();
    });

    // Handle chat finished
    newSocket.on('chat_finished', (data) => {
      console.log('✅ Chat finished:', data);
      // Fetch all data since chat status changed
      fetchAllData();
    });

    // Handle chat picked up
    newSocket.on('chat_picked_up', (data) => {
      console.log('👨‍💼 Chat picked up:', data);
      // Fetch waiting and active chats since chat status changed
      fetchWaitingAndActiveChats();
    });

    // Handle user info updates
    newSocket.on('user_info_updated', (data) => {
      console.log('👤 User info updated:', data);
      // Fetch waiting and active chats since user info changed
      fetchWaitingAndActiveChats();
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
      // Fallback to polling if socket fails
      setTimeout(() => {
        console.log('🔄 Falling back to polling due to socket error');
        startPollingFallback();
      }, 5000);
    });
  };

  // Fallback polling mechanism in case WebSocket fails
  const startPollingFallback = () => {
    console.log('🔄 Starting fallback polling every 10 seconds');
    const interval = setInterval(() => {
      fetchAllData();
    }, 10000);
    
    // Clean up interval when component unmounts
    return () => clearInterval(interval);
  };

  // Fetch only waiting chats
  const fetchWaitingChats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/support/waiting`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setWaitingChats(response.data);
      setLastUpdate(new Date());
      console.log('✅ Waiting chats updated via WebSocket');
    } catch (error) {
      console.error('❌ Failed to fetch waiting chats:', error);
    }
  };

  // Fetch waiting and active chats
  const fetchWaitingAndActiveChats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const [waitingResponse, activeResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/support/waiting`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/support/active`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setWaitingChats(waitingResponse.data);
      setActiveChats(activeResponse.data);
      setLastUpdate(new Date());
      console.log('✅ Waiting and active chats updated via WebSocket');
    } catch (error) {
      console.error('❌ Failed to fetch waiting and active chats:', error);
    }
  };

  // Fetch all data (for major changes like chat status changes)
  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🔄 Fetching all dashboard data...');
      setLoading(true);

      const [waitingResponse, activeResponse, transcriptsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/support/waiting`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/support/active`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/support/transcripts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log('✅ All dashboard data fetched successfully');
      console.log(`📊 Waiting chats: ${waitingResponse.data.length}`);
      console.log(`📊 Active chats: ${activeResponse.data.length}`);
      console.log(`📊 Transcripts: ${transcriptsResponse.data.length}`);
      setWaitingChats(waitingResponse.data);
      setActiveChats(activeResponse.data);
      setTranscripts(transcriptsResponse.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (error) {
      console.error('❌ Failed to fetch dashboard data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh function (for the refresh button)
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🔄 Manual refresh triggered...');
      setLoading(true);

      const [waitingResponse, activeResponse, transcriptsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/support/waiting`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/support/active`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/support/transcripts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log('✅ Manual refresh completed successfully');
      setWaitingChats(waitingResponse.data);
      setActiveChats(activeResponse.data);
      setTranscripts(transcriptsResponse.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (error) {
      console.error('❌ Failed to fetch dashboard data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  return (
    <Box>
      <AppBar 
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          boxShadow: '0 4px 20px rgba(0, 170, 229, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '70px' }}>
          {/* Left side - User Info */}
          {user && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 1,
              borderRadius: '8px',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              mr: 3
            }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                Welcome, {user.username}
              </Typography>
            </Box>
          )}
          
          {/* Center - Title */}
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.5rem',
              textAlign: 'center'
            }}
          >
            Support Dashboard
          </Typography>
          
          {/* Right side - Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Real-time status indicator */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1,
              borderRadius: '8px',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              fontSize: '0.75rem',
              color: 'white'
            }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: socket?.connected ? '#10b981' : '#ef4444',
                animation: socket?.connected ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {socket?.connected ? 'Live' : 'Offline'}
              </Typography>
            </Box>
            
            {/* Last update indicator */}
            {lastUpdate && (
              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.7rem'
              }}>
                Updated: {lastUpdate.toLocaleTimeString()}
              </Typography>
            )}
            
            <IconButton 
              onClick={fetchData} 
              disabled={loading}
              sx={{ 
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              title="Manual refresh"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton 
              onClick={toggleSound} 
              sx={{ 
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: soundEnabled ? 'white' : 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              title={soundEnabled ? 'Sound On - Click to disable' : 'Sound Off - Click to enable'}
            >
              {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
            </IconButton>
            
            {/* Change Password Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleChangePassword}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Change Password
            </Button>
            
            {/* Logout Button */}
            <Button
              variant="contained"
              size="small"
              onClick={handleLogout}
              sx={{
                bgcolor: 'rgba(220, 38, 38, 0.8)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(220, 38, 38, 1)'
                },
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 8, mb: 4, p: 3 }}>
        {error && (
          <Box sx={{ 
            mb: 3, 
            p: 2.5, 
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            color: '#dc2626',
            borderRadius: '12px',
            border: '1px solid #fecaca',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.1)'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Error: {error}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3} justifyContent="center">
          {/* Waiting Chats */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  p: 2,
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  borderRadius: '15px',
                  color: 'white'
                }}>
                  <Badge 
                    badgeContent={waitingChats.length} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        background: 'white',
                        color: '#f59e0b',
                        fontWeight: 600
                      }
                    }}
                  >
                    <NotificationsIcon sx={{ fontSize: 28 }} />
                  </Badge>
                  <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                    Waiting Chats ({waitingChats.length})
                  </Typography>
                  <Chip 
                    label="Real-time" 
                    size="small" 
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontSize: '0.6rem',
                      height: 20
                    }} 
                  />
                </Box>
                
                <Box sx={{ textAlign: 'center', py: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/waiting')}
                    sx={{
                      background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                      borderRadius: '12px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                        transform: 'scale(1.02)',
                        boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    View All Waiting Chats ({waitingChats.length})
                  </Button>

                  {loading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CircularProgress sx={{ color: '#f59e0b', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Fetching waiting chats...
                      </Typography>
                    </Box>
                  ) : waitingChats.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <NotificationsIcon sx={{ 
                        fontSize: 48, 
                        color: '#cbd5e0',
                        mb: 1
                      }} />
                      <Typography variant="h6" sx={{ 
                        color: '#64748b',
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        No waiting chats
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        All customers are being helped
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Active Chats */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  p: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '15px',
                  color: 'white'
                }}>
                  <ChatIcon sx={{ fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Active Chats ({activeChats.length})
                  </Typography>
                  <Chip 
                    label="Real-time" 
                    size="small" 
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontSize: '0.6rem',
                      height: 20
                    }} 
                  />
                </Box>
                
                                                 <Box sx={{ textAlign: 'center', py: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/chats')}
                    sx={{
                      background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                      borderRadius: '12px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                        transform: 'scale(1.02)',
                        boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    View All Active Chats ({activeChats.length})
                  </Button>

                  {loading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CircularProgress sx={{ color: '#10b981', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Fetching active chats...
                      </Typography>
                    </Box>
                  ) : activeChats.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <ChatIcon sx={{ 
                        fontSize: 48, 
                        color: '#cbd5e0',
                        mb: 1
                      }} />
                      <Typography variant="h6" sx={{ 
                        color: '#64748b',
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        No active chats
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        No ongoing conversations
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Transcripts */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  p: 2,
                  background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                  borderRadius: '15px',
                  color: 'white'
                }}>
                  <DescriptionIcon sx={{ fontSize: 28, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Transcripts ({transcripts.length})
                  </Typography>
                  <Chip 
                    label="Real-time" 
                    size="small" 
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontSize: '0.6rem',
                      height: 20
                    }} 
                  />
                </Box>
                
                                                 <Box sx={{ textAlign: 'center', py: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/transcripts')}
                    sx={{
                      background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                      borderRadius: '12px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                        transform: 'scale(1.02)',
                        boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    View All Transcripts ({transcripts.length})
                  </Button>

                  {loading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CircularProgress sx={{ color: '#00aae5', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Fetching transcripts...
                      </Typography>
                    </Box>
                  ) : transcripts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <DescriptionIcon sx={{ 
                        fontSize: 48, 
                        color: '#cbd5e0',
                        mb: 1
                      }} />
                      <Typography variant="h6" sx={{ 
                        color: '#64748b',
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        No transcripts
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        No completed conversations yet
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Change Password Dialog */}
      <Dialog 
        open={showChangePassword} 
        onClose={handleChangePasswordClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {changePasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {changePasswordError}
            </Alert>
          )}
          {changePasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {changePasswordSuccess}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrent ? 'text' : 'password'}
            value={changePasswordData.currentPassword}
            onChange={(e) => setChangePasswordData(prev => ({
              ...prev,
              currentPassword: e.target.value
            }))}
            sx={{ mt: 1, mb: 2 }}
            disabled={isChangingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showCurrent ? 'Hide' : 'Show'}>
                    <IconButton edge="end" onClick={() => setShowCurrent(!showCurrent)}>
                      {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          
          <TextField
            fullWidth
            label="New Password"
            type={showNew ? 'text' : 'password'}
            value={changePasswordData.newPassword}
            onChange={(e) => {
              const value = e.target.value;
              setChangePasswordData(prev => ({ ...prev, newPassword: value }));
              setPasswordStrength(evaluatePasswordStrength(value));
            }}
            sx={{ mb: 2 }}
            disabled={isChangingPassword}
            helperText="Password must be at least 6 characters long"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showNew ? 'Hide' : 'Show'}>
                    <IconButton edge="end" onClick={() => setShowNew(!showNew)}>
                      {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          {passwordStrength.label && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={passwordStrength.percent} sx={{ height: 8, borderRadius: 1, '& .MuiLinearProgress-bar': { backgroundColor: passwordStrength.color } }} />
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#64748b' }}>Strength: {passwordStrength.label}</Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirm ? 'text' : 'password'}
            value={changePasswordData.confirmPassword}
            onChange={(e) => setChangePasswordData(prev => ({
              ...prev,
              confirmPassword: e.target.value
            }))}
            disabled={isChangingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showConfirm ? 'Hide' : 'Show'}>
                    <IconButton edge="end" onClick={() => setShowConfirm(!showConfirm)}>
                      {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleChangePasswordClose}
            disabled={isChangingPassword}
            sx={{ color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleChangePasswordSubmit}
            variant="contained"
            disabled={isChangingPassword}
            sx={{
              background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)'
              }
            }}
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportDashboard;
